import { useState, useRef, useCallback } from 'react';
import api from './api';
import { useFilePreview, FilePreviewModal } from './FilePreview';

// ─── Constants ───────────────────────────────────────────────────────────────

const ROLE_COLORS = {
  'Admin':           'var(--accent)',
  'Project Manager': 'var(--info-text)',
  'Finance':         'var(--purple-text)',
  'HR':              'var(--warning-text)',
  'Crew':            'var(--success-text)',
  'Client':          '#06b6d4',
};

const ALL_ROLES = ['Admin', 'Project Manager', 'HR', 'Finance', 'Crew', 'Client'];

const createFolder = (name, access = ['Admin'], locked = false, type = 'custom') => ({
  id: `folder_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  name,
  type,
  locked,
  access,
  files: [],
  subfolders: [],
  isOpen: false,
});

const DEFAULT_FOLDERS = [
  {
    ...createFolder('Project Documents', ['Admin', 'Project Manager', 'Crew'], true, 'standard'),
    id: 'project_documents',
    description: 'Technical docs, drawings, project info. Auto-access for confirmed crew.',
  },
  {
    ...createFolder('Finance', ['Admin', 'Finance'], true, 'standard'),
    id: 'finance',
    description: 'Quotations, crew plans, PO, invoices. Admin & Finance only.',
  },
  {
    ...createFolder('Crew', ['Admin'], true, 'crew'),
    id: 'crew',
    description: 'Auto-populated from confirmed crew members.',
    subfolders: [],
  },
  {
    ...createFolder('Client Folder', ['Admin', 'Client'], true, 'client'),
    id: 'client',
    description: 'Shared with client via Google Drive link.',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const updateFolderById = (folders, id, updater) =>
  folders.map(f =>
    f.id === id
      ? updater(f)
      : { ...f, subfolders: updateFolderById(f.subfolders, id, updater) }
  );

const deleteFolderById = (folders, id) =>
  folders
    .filter(f => f.id !== id)
    .map(f => ({ ...f, subfolders: deleteFolderById(f.subfolders, id) }));

const fileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼️';
  if (['pdf'].includes(ext)) return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  if (['zip','rar','7z'].includes(ext)) return '🗜️';
  if (['mp4','mov','avi'].includes(ext)) return '🎬';
  return '📎';
};

// ─── Role Badge ───────────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const color = ROLE_COLORS[role] || 'var(--text-dim)';
  return (
    <span style={{
      background: color + '22',
      color,
      border: `1px solid ${color}`,
      borderRadius: '4px',
      padding: '1px 6px',
      fontSize: '10px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
    }}>
      {role}
    </span>
  );
}

// ─── Access Editor ────────────────────────────────────────────────────────────

function AccessEditor({ access, onChange, onClose }) {
  return (
    <div style={{
      background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px',
      padding: '12px 16px', position: 'absolute', top: '28px', right: 0,
      zIndex: 400, minWidth: '180px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    }}>
      <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
        Access Control
      </div>
      {ALL_ROLES.map(role => (
        <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-body)', fontSize: '13px', padding: '3px 0', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={access.includes(role)}
            onChange={() => {
              const next = access.includes(role)
                ? access.filter(r => r !== role)
                : [...access, role];
              onChange(next);
            }}
          />
          {role}
        </label>
      ))}
      <div onClick={onClose} style={{ color: 'var(--text-dim)', fontSize: '11px', marginTop: '8px', cursor: 'pointer', borderTop: '1px solid var(--border)', paddingTop: '6px' }}>
        Close
      </div>
    </div>
  );
}

// ─── File Row ─────────────────────────────────────────────────────────────────

function FileRow({ file, onRemove, projectId }) {
  const { previewFile, openPreview, closePreview } = useFilePreview();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '6px 10px', borderRadius: '6px', background: 'var(--bg-card)',
      border: '1px solid var(--bg-subtle)', marginBottom: '4px',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--bg-subtle)'}>
      <span style={{ fontSize: '16px' }}>{fileIcon(file.name)}</span>
      {file.serverId && projectId ? (
        <span
          onClick={() => openPreview({
            url: api.projects.fileUrl(projectId, file.serverId),
            downloadUrl: api.projects.fileUrl(projectId, file.serverId, { download: true }),
            name: file.name,
          })}
          style={{ color: 'var(--info-text)', fontSize: '13px', flex: 1, cursor: 'pointer' }}>
          {file.name}
        </span>
      ) : (
        <span style={{ color: 'var(--text-body)', fontSize: '13px', flex: 1 }}>
          {file.name}
          {file._file && <span style={{ color: 'var(--warning-text)', fontSize: '10px', marginLeft: '8px' }} title="Завантажиться після збереження проєкту">⏳ pending</span>}
        </span>
      )}
      <span style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>{file.size}</span>
      <span
        onClick={() => onRemove(file.id)}
        style={{ color: 'var(--text-disabled)', fontSize: '12px', cursor: 'pointer', padding: '2px 6px' }}
        onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-disabled)'}>
        ✕
      </span>
      <FilePreviewModal file={previewFile} onClose={closePreview} />
    </div>
  );
}

// Профільний документ фрілансера (паспорт/фото/CV/інше) — живе посилання на
// freelancer_documents (не project_files), тому без кнопки видалення тут:
// прибрати/замінити такий файл фрілансер може лише сам, через свій Profile.
function LinkedFileRow({ doc, freelancerId }) {
  const { previewFile, openPreview, closePreview } = useFilePreview();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '6px 10px', borderRadius: '6px', background: 'var(--bg-card)',
      border: '1px dashed var(--bg-subtle)', marginBottom: '4px',
    }}>
      <span style={{ fontSize: '16px' }}>{fileIcon(doc.name)}</span>
      <span
        onClick={() => openPreview({
          url: api.freelancers.documentUrl(freelancerId, doc.id),
          downloadUrl: api.freelancers.documentUrl(freelancerId, doc.id, { download: true }),
          name: doc.name,
        })}
        style={{ color: 'var(--info-text)', fontSize: '13px', flex: 1, cursor: 'pointer' }}>
        {doc.name}
      </span>
      <span style={{ color: 'var(--text-disabled)', fontSize: '10px', textTransform: 'uppercase' }}>{doc.category}</span>
      <span style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>From profile</span>
      <FilePreviewModal file={previewFile} onClose={closePreview} />
    </div>
  );
}

// ─── Drop Zone ────────────────────────────────────────────────────────────────

function DropZone({ onFiles }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  // Зберігаємо і метадані, і сам File (_file) — він завантажиться на сервер
  const toMeta = f => ({
    id: `file_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    name: f.name,
    size: f.size > 1024 * 1024
      ? `${(f.size / 1024 / 1024).toFixed(1)} MB`
      : `${Math.round(f.size / 1024)} KB`,
    type: f.name.split('.').pop(),
    _file: f,
  });

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    onFiles(Array.from(e.dataTransfer.files).map(toMeta));
  }, [onFiles]);

  const handleChange = e => {
    const files = Array.from(e.target.files).map(toMeta); // синхронно, до reset
    onFiles(files);
    e.target.value = '';
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current.click()}
      style={{
        border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border-strong)'}`,
        borderRadius: '8px',
        padding: '12px',
        textAlign: 'center',
        color: dragging ? 'var(--accent)' : 'var(--text-disabled)',
        fontSize: '12px',
        cursor: 'pointer',
        background: dragging ? 'var(--danger-bg)' : 'transparent',
        transition: 'all 0.15s',
        marginTop: '8px',
      }}>
      {dragging ? 'Drop files here' : '+ Drop files or click to upload'}
      <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={handleChange} />
    </div>
  );
}

// ─── Folder Item (recursive) ──────────────────────────────────────────────────

function FolderItem({ folder, depth = 0, onUpdate, onDelete, projectId }) {
  const [showAccess, setShowAccess] = useState(false);
  const [addingFolder, setAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isOther, setIsOther] = useState(false);

  const toggle = () => onUpdate(folder.id, f => ({ ...f, isOpen: !f.isOpen }));

  const addFiles = files => {
    onUpdate(folder.id, f => ({ ...f, files: [...f.files, ...files] }));
  };

  const removeFile = fileId => {
    const file = folder.files.find(x => x.id === fileId);
    // Файл уже на сервері — видалити і там
    if (file?.serverId && projectId) {
      api.projects.deleteFile(projectId, file.serverId).catch(() => {});
    }
    onUpdate(folder.id, f => ({ ...f, files: f.files.filter(x => x.id !== fileId) }));
  };

  const updateAccess = access => {
    onUpdate(folder.id, f => ({ ...f, access }));
  };

  const addSubfolder = () => {
    if (!newFolderName.trim()) return;
    const sub = createFolder(newFolderName.trim(), ['Admin']);
    onUpdate(folder.id, f => ({ ...f, subfolders: [...f.subfolders, sub], isOpen: true }));
    setNewFolderName('');
    setAddingFolder(false);
    setIsOther(false);
  };

  const STANDARD_SUBFOLDER_NAMES = ['Project Documents', 'Finance', 'Crew', 'Client', 'Other...'];

  return (
    <div style={{ marginLeft: depth > 0 ? '20px' : '0', borderLeft: depth > 0 ? '1px solid var(--border)' : 'none', paddingLeft: depth > 0 ? '12px' : '0' }}>
      {/* Folder header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '8px 10px', borderRadius: '8px',
        background: folder.isOpen ? 'var(--bg-subtle)' : 'transparent',
        cursor: 'pointer',
        marginBottom: '2px',
      }}
        onMouseEnter={e => { if (!folder.isOpen) e.currentTarget.style.background = 'var(--bg-card)'; }}
        onMouseLeave={e => { if (!folder.isOpen) e.currentTarget.style.background = 'transparent'; }}>

        <span onClick={toggle} style={{ fontSize: '12px', color: 'var(--text-dim)', width: '12px' }}>
          {folder.isOpen ? '▼' : '▶'}
        </span>
        <span onClick={toggle} style={{ fontSize: '18px' }}>
          {folder.type === 'crew' || folder.type === 'crew_member' ? '👥' : folder.type === 'client' ? '🔗' : '📁'}
        </span>
        <span onClick={toggle} style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: folder.locked ? 'bold' : 'normal', flex: 1 }}>
          {folder.name}
        </span>

        {/* Access badges */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {folder.access.map(r => <RoleBadge key={r} role={r} />)}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '4px', position: 'relative' }}>
          <button
            onClick={e => { e.stopPropagation(); setShowAccess(p => !p); }}
            title="Edit access"
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>
            🔐
          </button>
          <button
            onClick={e => { e.stopPropagation(); setAddingFolder(p => !p); }}
            title="Add subfolder"
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>
            + 📁
          </button>
          {!folder.locked && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(folder.id); }}
              title="Delete folder"
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dim)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}>
              🗑
            </button>
          )}

          {showAccess && (
            <AccessEditor
              access={folder.access}
              onChange={updateAccess}
              onClose={() => setShowAccess(false)}
            />
          )}
        </div>
      </div>

      {/* Add subfolder input */}
      {addingFolder && (
        <div style={{ marginLeft: '32px', marginBottom: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          {!isOther ? (
            <>
              <select
                autoFocus
                style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '6px 10px', fontSize: '13px' }}
                value=""
                onChange={e => {
                  if (e.target.value === 'Other...') { setIsOther(true); setNewFolderName(''); }
                  else { setNewFolderName(e.target.value); }
                }}>
                <option value="">Select or type...</option>
                {STANDARD_SUBFOLDER_NAMES.map(n => <option key={n}>{n}</option>)}
              </select>
              {newFolderName && newFolderName !== 'Other...' && (
                <button onClick={addSubfolder} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  Add
                </button>
              )}
            </>
          ) : (
            <>
              <input
                autoFocus
                placeholder="Folder name..."
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addSubfolder()}
                style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-primary)', padding: '6px 10px', fontSize: '13px', outline: 'none' }}
              />
              <button onClick={addSubfolder} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                Add
              </button>
            </>
          )}
          <button onClick={() => { setAddingFolder(false); setNewFolderName(''); setIsOther(false); }}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}

      {/* Folder contents */}
      {folder.isOpen && (
        <div style={{ marginLeft: '32px', marginBottom: '8px' }}>
          {folder.description && (
            <p style={{ color: 'var(--text-disabled)', fontSize: '11px', margin: '0 0 8px 0', fontStyle: 'italic' }}>{folder.description}</p>
          )}

          {/* Subfolders */}
          {folder.subfolders.map(sub => (
            <FolderItem
              key={sub.id}
              folder={sub}
              depth={depth + 1}
              onUpdate={onUpdate}
              onDelete={onDelete}
              projectId={projectId}
            />
          ))}

          {/* Профільні документи фрілансера (паспорт/фото/CV/інше) — підмішані
              бекендом лише для читання, не зберігаються в docFolders. */}
          {(folder.linkedDocuments || []).map(doc => (
            <LinkedFileRow key={`linked-${doc.id}`} doc={doc} freelancerId={folder.freelancerId} />
          ))}

          {/* Files */}
          {folder.files.map(file => (
            <FileRow key={file.id} file={file} onRemove={removeFile} projectId={projectId} />
          ))}

          {/* Drop zone */}
          <DropZone onFiles={addFiles} />
        </div>
      )}
    </div>
  );
}

// ─── Documents Section ────────────────────────────────────────────────────────

// Дефолтне дерево папок (+ підпапки для призначених фрілансерів)
export function buildDefaultDocFolders(assignedFreelancers = []) {
  const defaults = JSON.parse(JSON.stringify(DEFAULT_FOLDERS));
  const crewFolder = defaults.find(f => f.id === 'crew');
  if (crewFolder && assignedFreelancers.length > 0) {
    const existingNames = crewFolder.subfolders.map(s => s.name);
    assignedFreelancers.forEach(name => {
      if (!existingNames.includes(name)) {
        crewFolder.subfolders.push({
          ...createFolder(name, ['Admin', 'Crew'], false, 'crew_member'),
          files: [],
          subfolders: [],
          isOpen: false,
        });
      }
    });
  }
  return defaults;
}

function DocumentsSection({ folders: foldersProp, onChange, projectId, assignedFreelancers = [] }) {
  // Контрольований режим (з ProjectForm): folders + onChange.
  // Автономний (legacy): внутрішній стан.
  const [localFolders, setLocalFolders] = useState(() => buildDefaultDocFolders(assignedFreelancers));
  const folders = foldersProp ?? localFolders;
  const setFolders = useCallback(updater => {
    if (onChange) onChange(typeof updater === 'function' ? updater : () => updater);
    else setLocalFolders(updater);
  }, [onChange]);

  const [addingRoot, setAddingRoot] = useState(false);
  const [rootName, setRootName] = useState('');
  const [rootIsOther, setRootIsOther] = useState(false);

  const updateFolder = useCallback((id, updater) => {
    setFolders(prev => updateFolderById(prev, id, updater));
  }, [setFolders]);

  const deleteFolder = useCallback((id) => {
    setFolders(prev => deleteFolderById(prev, id));
  }, [setFolders]);

  const addRootFolder = () => {
    if (!rootName.trim()) return;
    setFolders(prev => [...prev, createFolder(rootName.trim(), ['Admin'])]);
    setRootName('');
    setAddingRoot(false);
    setRootIsOther(false);
  };

  const ROOT_FOLDER_NAMES = ['Project Documents', 'Finance', 'Crew', 'Client Folder', 'Other...'];

  return (
    <div>
      {/* Folder list */}
      <div style={{ marginBottom: '12px' }}>
        {folders.map(folder => (
          <FolderItem
            key={folder.id}
            folder={folder}
            depth={0}
            onUpdate={updateFolder}
            onDelete={deleteFolder}
            projectId={projectId}
          />
        ))}
      </div>

      {/* Add root folder */}
      {addingRoot ? (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
          {!rootIsOther ? (
            <>
              <select
                autoFocus
                style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '6px 10px', fontSize: '13px' }}
                value=""
                onChange={e => {
                  if (e.target.value === 'Other...') { setRootIsOther(true); setRootName(''); }
                  else setRootName(e.target.value);
                }}>
                <option value="">Select folder type...</option>
                {ROOT_FOLDER_NAMES.map(n => <option key={n}>{n}</option>)}
              </select>
              {rootName && rootName !== 'Other...' && (
                <button onClick={addRootFolder} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  Add
                </button>
              )}
            </>
          ) : (
            <>
              <input
                autoFocus
                placeholder="Folder name..."
                value={rootName}
                onChange={e => setRootName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRootFolder()}
                style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-primary)', padding: '6px 10px', fontSize: '13px', outline: 'none' }}
              />
              <button onClick={addRootFolder} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                Add
              </button>
            </>
          )}
          <button onClick={() => { setAddingRoot(false); setRootName(''); setRootIsOther(false); }}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAddingRoot(true)}
          style={{
            background: 'transparent', border: '1px dashed var(--border-strong)', color: 'var(--text-dim)',
            padding: '10px', borderRadius: '8px', cursor: 'pointer',
            fontSize: '13px', width: '100%', marginTop: '4px',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-dim)'; }}>
          + Add Folder
        </button>
      )}
    </div>
  );
}

export default DocumentsSection;